import { Bar, CartesianGrid, Cell as PieCell, ComposedChart, Line, Pie, PieChart, Tooltip, XAxis, YAxis } from 'recharts'
import { Clock, DollarSign, Layers, ShoppingCart, TrendingUp, Undo2, Wallet } from 'lucide-react'
import { money } from '@/lib/money'
import { BAR_RADIUS, CHART, compactNumber, gridProps, tooltipProps, xAxisProps, yAxisProps } from '@/lib/chart'
import type { SalesReport } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import StatTile from '@/components/ui/StatTile'
import { ChartBox, ChartEmpty, ChartLegend } from '@/components/ui/Chart'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'

const icon = 'size-5'

const label = (status: string) => status.replace(/_/g, ' ')

export default function SalesTab({ data }: { data: SalesReport }) {
  const t = data.totals
  const hasSales = data.series.some((s) => s.orders > 0)
  const totalStatus = data.by_status.reduce((sum, s) => sum + s.count, 0)

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Orders" value={t.orders} icon={<ShoppingCart className={icon} />} tone="primary" hint="not cancelled" />
        <StatTile label="Revenue" value={money(t.revenue)} icon={<DollarSign className={icon} />} tone="success" hint="incl. credit" />
        <StatTile label="Pieces ordered" value={t.pieces} icon={<Layers className={icon} />} tone="pending" />
        <StatTile label="Avg order value" value={money(t.avg_order_value)} icon={<TrendingUp className={icon} />} tone="warning" />
      </div>
      {/* Booked sales above; where the money actually is below. Absent on a
          payload from before payment status existed. */}
      {t.collected !== undefined && (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile label="Collected" value={money(t.collected)} icon={<Wallet className={icon} />} tone="success" hint="money received" />
          <StatTile label="Outstanding" value={money(t.outstanding)} icon={<Clock className={icon} />} tone="warning" hint="on credit, unpaid" />
          <StatTile label="Refunds due" value={money(t.refunds_due)} icon={<Undo2 className={icon} />} tone="pending" hint="owed back" />
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <SectionLabel>Revenue and orders per {data.granularity}</SectionLabel>
          {hasSales ? (
            <>
              <ChartBox>
                <ComposedChart data={data.series}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="label" {...xAxisProps} />
                  <YAxis yAxisId="left" {...yAxisProps} width={48} tickFormatter={compactNumber} />
                  <YAxis yAxisId="right" orientation="right" allowDecimals={false} {...yAxisProps} />
                  <Tooltip
                    {...tooltipProps}
                    formatter={(value, name) => (name === 'Revenue' ? money(Number(value)) : String(value))}
                  />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill={CHART.primary} radius={BAR_RADIUS} />
                  <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke={CHART.success} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ChartBox>
              <ChartLegend items={[{ label: 'Revenue, RM (left)', fill: CHART.primary }, { label: 'Orders (right)', fill: CHART.success }]} />
            </>
          ) : (
            <ChartEmpty>No settled orders in this range.</ChartEmpty>
          )}
        </Card>

        <Card className="min-w-0">
          <SectionLabel>Orders by status</SectionLabel>
          {totalStatus > 0 ? (
            <>
              <ChartBox height={160}>
                <PieChart>
                  <Pie data={data.by_status} dataKey="count" nameKey="status" innerRadius="70%" outerRadius="95%" paddingAngle={4} cornerRadius={5}>
                    {data.by_status.map((_, i) => (
                      <PieCell key={i} fill={CHART.donut[i % CHART.donut.length]!.fill} stroke={CHART.donut[i % CHART.donut.length]!.stroke} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [String(value), label(String(name))]} />
                </PieChart>
              </ChartBox>
              <ChartLegend
                className="flex-col gap-y-1"
                items={data.by_status.map((s, i) => ({
                  label: `${label(s.status)} - ${s.count}`,
                  fill: CHART.donut[i % CHART.donut.length]!.fill,
                  stroke: CHART.donut[i % CHART.donut.length]!.stroke,
                }))}
              />
            </>
          ) : (
            <ChartEmpty height={160}>No orders in this range.</ChartEmpty>
          )}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <SectionLabel>Top customers</SectionLabel>
          <Table columns={['Customer', 'Orders', 'Revenue']}>
            {data.top_customers.map((row, i) => (
              <Row key={i}>
                <Cell>
                  <span className="font-semibold">{row.customer}</span>
                  {row.company_name && <span className="block text-2xs text-muted">{row.company_name}</span>}
                </Cell>
                <Cell>{row.orders}</Cell>
                <Cell className="font-semibold">{money(row.revenue)}</Cell>
              </Row>
            ))}
            {data.top_customers.length === 0 && <EmptyRow colSpan={3}>No settled orders in this range.</EmptyRow>}
          </Table>
        </div>
        <div className="min-w-0">
          <SectionLabel>Top products</SectionLabel>
          <Table columns={['Product', 'Qty', 'Revenue']}>
            {data.top_products.map((row, i) => (
              <Row key={i}>
                <Cell className="font-semibold">{row.product}</Cell>
                <Cell>{row.qty}</Cell>
                <Cell className="font-semibold">{money(row.revenue)}</Cell>
              </Row>
            ))}
            {data.top_products.length === 0 && <EmptyRow colSpan={3}>No settled orders in this range.</EmptyRow>}
          </Table>
        </div>
      </div>
    </>
  )
}
